import moment from 'moment';

import { getBrowserDiagnostics } from 'Utilities/browser';
import { getFromAccountType, setUrls, urls } from 'Utilities/dataHelpers';
import { timeStampFormat } from 'Utilities/helpers';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { getIn } from 'Utilities/newyu';
import {
  clearSession,
  getSessionObject,
  setSessionObject,
  updateChosenCountryLang,
} from 'Utilities/storage';
import { store } from 'Utilities/store';

const getStateString = () => {
  const rootStoreObject = store.getState();
  let stateString = '';

  stateString += `LibreView Version: ${process.env.VERSION}\n`;
  stateString += `Browser Information:\n`;
  stateString += JSON.stringify(getBrowserDiagnostics(), null, 2);
  stateString += '\n'; // new line for the browser info
  stateString += 'User Agent:\n';
  stateString += `\t${window.navigator.userAgent}\n`;
  stateString += 'Local Machine Time\n';
  stateString += `\t${timeStampFormat(undefined, '')}\n`;

  stateString += '\n'; // final new line

  const realpathStoreInfo = getFromAccountType(rootStoreObject.config.printStateModel);
  const pathStoreInfo = JSON.parse(JSON.stringify(realpathStoreInfo));

  for (const section of pathStoreInfo) {
    const sectionName = Object.keys(section);
    const statePathsArray = section[sectionName[0]];

    // handle dynamic state paths
    for (
      let singleStatePathIndex = 0;
      singleStatePathIndex < statePathsArray.length;
      singleStatePathIndex++
    ) {
      const singlePathArr = statePathsArray[singleStatePathIndex].split('.');

      // if the path has dynamic, add a bunch more state paths
      if (singlePathArr.includes('1337Dynamic')) {
        let currentStoreObject = rootStoreObject;

        for (const currentStateLevel of singlePathArr) {
          if (currentStateLevel === '1337Dynamic' && currentStoreObject) {
            // copy the single pathArr 1337Dynamic times
            for (const dynamicStorepath of Object.keys(currentStoreObject)) {
              statePathsArray.push(
                statePathsArray[singleStatePathIndex].replace('1337Dynamic', dynamicStorepath)
              );
            }

            break;
          }

          currentStoreObject = getIn(currentStoreObject, currentStateLevel);
        }

        // remove the placeholder path when finished
        statePathsArray.splice(singleStatePathIndex, 1);
        singleStatePathIndex--;
      }
    }

    stateString = stateString.concat(`${sectionName[0]}\n`);

    // loop through all state paths in the section array
    for (const singleStatePath of statePathsArray) {
      let currentStoreObject = rootStoreObject;
      const singlePathArr = singleStatePath.split('.');

      for (const currentStateLevel of singlePathArr) {
        currentStoreObject = getIn(currentStoreObject, currentStateLevel);
      }

      const stateLine = `\t${singleStatePath}: ${currentStoreObject}`;

      stateString = stateString.concat(`${stateLine}\n`);
    }
  }

  return stateString;
};

export const downloadState = (): void => {
  const stateString = getStateString();
  const datetime = timeStampFormat(undefined, 'YYYY-MM-DDTHH.mm.ssZZ');
  const filename = `lv-web-state-${datetime}.log`;

  console.log(stateString);

  if (confirm(`Download ${filename}?`)) {
    const fileBlob = new Blob([stateString], { type: 'text/csv' });

    mediator.publish('download:fromBlob', { blob: fileBlob, filename });
  }
};

export const copyStateToClipboard = (): void => {
  const el = document.createElement('textarea');

  el.value = getStateString();
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  alert('State copied to clipboard');
};

const _handle2fa = () => {
  return new Promise<LVApiResponse>((resolve, reject) => {
    const code = window.prompt('Enter 2FA code', '');

    if (code === null || code === '') {
      reject();
      return;
    }

    mediator
      .request('api:invoke', {
        method: 'POST',
        url: urls.lslApi + 'auth/continue/2fa/result',
        data: {
          code,
        },
      })
      .then((result) => {
        if (result.status === 0) {
          resolve(result);
        }

        reject();
      })
      .catch(() => {
        reject();
      });
  });
};

const _doLogin = (request: any) => {
  return (request.response = new Promise<LVApiResponse>((resolve, reject) => {
    mediator
      .request<any>('api:invoke', {
        method: 'POST',
        url: urls.lslApi + 'auth/login',
        data: {
          email: request.email,
          password: request.password,
          fingerprint: '',
        },
      })
      .then((result) => {
        if (result.status === 0) {
          if (result.data && result.data.redirect) {
            const { regionalMap = {} } = store.getState().config;

            const regionalUrls = regionalMap[result.data.region];

            if (regionalUrls) {
              setUrls(regionalUrls);

              // login again with the new url
              _doLogin(request);
              request.response
                .then((result: any) => resolve(result))
                .catch((err: any) => reject(err));
              return;
            }

            // TODO: bad state, lvis telling us wrong region
            // but no way to get there
          }

          mediator.publish('user:loginSuccess', result.data);
        } else if ([1, 2, 3].includes(result.status)) {
          log.error(result.error);
          reject(result.error);
          return;
        } else if (result.status === 4 && result.data.step.type === '2faVerify') {
          _handle2fa()
            .then((result) => {
              mediator.publish('user:loginSuccess', result.data);
              mediator.publish('router:navigate', '/');
              resolve(result);
            })
            .catch(() => reject());

          return;
        }

        resolve(result);
      })
      .catch((err) => {
        log.error(err);
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_AUTH_LOGIN',
          },
        });
        reject(err);
      });
  }));
};

/** Behind the scenes login helper */
const login = () => {
  const email = window.prompt('Email', getSessionObject('email') || '');
  const password = window.prompt('Password', getSessionObject('password') || '');

  if (email && password) {
    _doLogin({ email, password }).then((result) => {
      if (result.status === 0) {
        if (result.data.user.accountType !== 'pat') {
          log.warn('Only patient accounts can be logged into ll-webviews');
          clearSession();
          return;
        }

        updateChosenCountryLang(result.data.user.country, result.data.user.uiLanguage);
        setSessionObject('email', email);
        setSessionObject('password', password);
      }
    });
  }
};

const setLanguage = (lang?: string) => {
  if (lang) {
    mediator.publish('trans:change', lang);
  }
};

const setToken = () => {
  const token = window.prompt('User Token', '');

  setSessionObject('loggedIn', true);
  setSessionObject('token', {
    token,
    expires: moment().add(5, 'minutes').unix(),
  });

  window.location.reload();
};

declare global {
  interface Window {
    testHelper: {
      login: typeof login;
      setLanguage: typeof setLanguage;
      setToken: typeof setToken;
    };
  }
}

window.testHelper = {
  // appNavigate,
  // printState,
  login,
  setLanguage,
  setToken,
};
